import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertProps } from '@mui/material';

export interface ToastOptions {
  title?: string;
  description?: string;
  variant?: 'success' | 'info' | 'warning' | 'error' | 'destructive';
  duration?: number;
}

interface ToastContextType {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertProps['severity']>('info');
  const [duration, setDuration] = useState(6000);

  const toast = (options: ToastOptions) => {
    const { title, description, variant = 'info', duration: customDuration = 6000 } = options;
    
    // Map shadcn variants to MUI severity
    const muiSeverity: AlertProps['severity'] = variant === 'destructive' ? 'error' : variant;
    
    // Combine title and description
    const fullMessage = title && description ? `${title}: ${description}` : title || description || '';
    
    setMessage(fullMessage);
    setSeverity(muiSeverity);
    setDuration(customDuration);
    setOpen(true);
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
